import UIDatePicker from "@/components/ui-controls/date-field";
import UISelect from "@/components/ui-controls/select";
import { Box } from "@mui/material";
import moment from "moment";
import * as styles from "./styles.module.scss";
import { time } from "@/modules/crm/global/constants";
import { useCRMDataClient } from "@/services/data-client/DataClientContext";
import { useEffect, useState, useRef } from "react";
import CircularLoader from "@/components/circular-loader";
import { toast } from "react-toastify";


import {
  getBookingError,
  setBookingErrorToState,
} from "@/store/slices/appointment/appointmentSlice";
import { useDispatch , useSelector } from "react-redux";

const DateFields = ({
  onChange,
  form = {},
  timezones,
  isCalendarDisabled,
  isReadOnly = false,
  isRerendering = false,
  handleSlotDisable,
  offsetMinutes = 0,
}) => {
  const { client } = useCRMDataClient();
  const [dateAvailability, setDateAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [slotOptions, setSlotOptions] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [selectedSlotLabel, setSelectedSlotLabel] = useState("");
  const [isSlotDisabled, setIsSlotDisabled] = useState(true);
  const [isDateChanging, setIsDateChanging] = useState(false);
  const [loadingDays, setLoadingDays] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [currentMonthView, setCurrentMonthView] = useState(null);
  const dispatch = useDispatch();
  const bookingError = useSelector(getBookingError);
  const [shouldFetchSlots, setShouldFetchSlots] = useState(false);

  useEffect(() => {
    handleSlotDisable(isSlotDisabled || isDateChanging);
  }, [isSlotDisabled, isDateChanging]);



  const formatTime = (hour, minute) => {
    const ampm = hour >= 12 ? "PM" : "AM";
    const adjustedHour = hour % 12 || 12;
    const formattedMinute = minute.toString().padStart(2, "0");
    return `${adjustedHour}:${formattedMinute} ${ampm}`;
  };

  const handleSlotChange = (e) => {
    setSelectedSlotLabel("");
    const selected = slotOptions.find(
      (option) => option.value == e.target.value,
    );
    const types = ["startDate", "endDate"];
    if (selected.participantId) {
      onChange("participantId", selected.participantId);
    }
    types.forEach((timeType) => {
      // const timezoneTime = moment(selected[timeType], time.format.dateOnly).utc();
      // // const formattedDate = `${selected[timeType].getFullYear()}-${String(selected[timeType].getMonth() + 1).padStart(2, '0')}-${String(selected[timeType].getDate()).padStart(2, '0')}T${String(selected[timeType].getHours()).padStart(2, '0')}:${String(selected[timeType].getMinutes()).padStart(2, '0')}:${String(selected[timeType].getSeconds()).padStart(2, '0')}${selectedTimeZone.offset}`;
      // console.log(moment.utc(selected[timeType]).format(time.format.standardDate), selected[timeType],'i hate tears');
      // const localTime = moment.utc(selected[timeType]).format(time.format.standardDate)

      // const utcDate = selected[timeType];

      // const convertedTime = moment.utc(utcDate).tz(convertToTimezoneISO(form.values?.timeZone));

      // const formattedWithTimeZone = convertedTime.format(time.format.isoStringFormat);
      // console.log(selected[timeType], 'selectedTimeOffset');
      // console.log(selected[timeType]);

      onChange(timeType, selected[timeType]);
    });
    form.handleChange(e);
  };

  const fetchAvailabilityForMonth = async (month) => {
    dispatch(setBookingErrorToState(""));
    const daysInMonth = moment(month).daysInMonth();

    const datesToCheck = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = moment(month).date(day);
      if (date.isSameOrAfter(moment().startOf("day"))) {
        datesToCheck.push(date);
      }
    }
    setLoadingDays(
      datesToCheck.map((date) => date.format(time.format.dateOnly)),
    );

    let daysSelectableMap = {};
    try {
      daysSelectableMap = await client.appointments.getAvailabilityByDate({
        date: moment(month).startOf("month").format(time.format.dateOnly),
        calendarId: form.values?.calendarId?.toString(),
        timeZoneId: form.values?.timeZone,
        offsetMinutes,
      });
      if (daysSelectableMap?.errorMessage) {
        dispatch(setBookingErrorToState(daysSelectableMap.errorMessage));
      }
    } catch (error) {
      console.error(error);
      dispatch(setBookingErrorToState(error.message));
    }

    const availabilityResults = datesToCheck.map((date) => {
      const dateStr = date.format(time.format.dateOnly);
      return !!daysSelectableMap?.[dateStr];
    });

    let savedDates = Object.values(dateAvailability);
    let allDates = [...savedDates, ...availabilityResults];
    if (availabilityResults.every((item) => !item)) {
      if (bookingError) {
        toast.error(bookingError, {
          toastId: "invalid-date-error",
        });
        dispatch(setBookingErrorToState(""));
      }
    }
    const newDateAvailability = {};
    datesToCheck.forEach((date, index) => {
      newDateAvailability[date.format(time.format.dateOnly)] =
        availabilityResults[index];
    });

    setDateAvailability((prev) => ({ ...prev, ...newDateAvailability }));
    setLoadingDays([]);
  };

  const shouldDisableDate = (date) => {
    const today = moment().startOf("day");
    if (date.isBefore(today, "day")) {
      return true;
    }
    const dateISOString = date.local()?.format(time.format.dateOnly);
    if (dateAvailability[dateISOString] !== undefined) {
      return !dateAvailability[dateISOString];
    }
    return true;
  };

  useEffect(() => {
    if (isCalendarDisabled || !form.values?.calendarId) return;

    let startMonth = moment().startOf("month");
    const effectiveDate = getEffectiveDate();
    if (effectiveDate.isValid() && effectiveDate.isAfter(startMonth)) {
      startMonth = moment(effectiveDate).startOf("month");
    }

    const monthString = startMonth.format("YYYY-MM");
    setCurrentMonthView(startMonth);
    setAvailableMonths([monthString]);
    fetchAvailabilityForMonth(startMonth);
  }, [isCalendarDisabled, form.values?.calendarId, form.values?.timeZone, offsetMinutes]);

  // Cleanup function to avoid setting state on unmounted component
  useEffect(() => {
    return () => {
      setDateAvailability({});
      setLoadingDays([]);
      setAvailableMonths([]);
      dispatch(setBookingErrorToState(""));
    };
  }, [form.values?.calendarId]);

  const getEffectiveDate = () => {
    let date = form?.values?.date || form?.values?.startDate;
    // Always parse as UTC to prevent local timezone shifts
    return moment.isMoment(date) ? date : moment.utc(date);
  };

  const isSameMonth = form?.values?.date && currentMonthView && moment(form.values.date).isSame(currentMonthView, "month");
  const calendarValue = isSameMonth && moment(form.values.date).isValid() ? moment(form.values.date) : null;

  const effectDate = getEffectiveDate();
  const effectDateISO = effectDate.isValid()
    ? effectDate.format(time.format.dateOnly)
    : null;
  const isDateLoading = effectDateISO && loadingDays.includes(effectDateISO);
  const isDateAvailable = effectDateISO && dateAvailability[effectDateISO];

  useEffect(() => {
    if (isCalendarDisabled) return;
    const fieldDate = moment.utc(form?.values?.date);
    const startDate = moment.utc(form?.values?.startDate);

    if (fieldDate.isSame(startDate, "day") && !shouldFetchSlots  && slotOptions.length > 0) return;

    const date = getEffectiveDate();

    if (date.isValid() && effectDateISO) {
      if (isDateLoading) {
        setSlotOptions([]); 
        setIsDateChanging(false);
        return;
      }
      if (isDateAvailable === false) {
        setSlotOptions([]); 
        setIsDateChanging(false);
        return;
      }

      if (shouldFetchSlots) {
        setShouldFetchSlots(false);
      }

      const payload = {
        date: effectDateISO,
        calendarId: form.values?.calendarId?.toString(),
        timeZoneId: form.values?.timeZone,
        offsetMinutes,
      };
      setIsDateChanging(true);
      client.appointments.getSlotsByDate(payload)
        .then((data) => {
          if (data?.timeSlot) {
            const slots = data?.timeSlot?.items;
            const formatted = (d) => d.format(time.format.isoStringFormat);
            const isSame = slots?.findIndex(
              (slot) => {
                const storedStart = form.values?.startDate;
                if (!storedStart) return false;
                return formatted(moment.utc(storedStart)) == formatted(moment.utc(slot.startDate));
              }
            );
            if (slots?.length) {
              setIsSlotDisabled(false);
              const mappedSlots = slots.map((slot) => ({
                startDate: slot.startDate,
                endDate: slot.endDate,
                participantId: slot.participantId,
                label: `${formatTime(slot.startHour, slot.startMinute)} - ${formatTime(slot.endHour, slot.endMinute)}`,
                value: `${slot.startHour}${slot.startMinute}${slot.endHour}${slot.endMinute}`,
              }));

              const uniqueSlots = [];
              const seenSlots = new Set();
              mappedSlots.forEach((slot) => {
                if (!seenSlots.has(slot.value)) {
                  seenSlots.add(slot.value);
                  uniqueSlots.push(slot);
                }
              });

              setSlotOptions(uniqueSlots);

              if (isSame == -1 && !isReadOnly) {
                form.setFieldValue("startDate", "");
                form.setFieldValue("endDate", "");
                setSelectedSlot("");
                setSelectedSlotLabel("");
              }
            } else {
              setSlotOptions([]);
              setIsSlotDisabled(true);
              if (!isReadOnly && !form?.values?.startDate) {
                toast.warn(
                  `No slots available for ${moment(date).format(time.format.date)}`,
                  { toastId: "no-slots-available" },
                );
              }
            }
          }
          setIsDateChanging(false);
        })
        .catch((err) => {
          console.error("Error fetching slots:", err);
          setSlotOptions([]);
          setIsSlotDisabled(true);
          setIsDateChanging(false);
          toast.error("Failed to fetch available slots. Please try again.");
        });
    }

    return () => {};
  }, [
    isCalendarDisabled,
    loadingDays,
    dateAvailability,
    shouldFetchSlots,
    effectDateISO,
  ]);

  useEffect(() => {
    return () => {
      setSlotOptions([]);
      setSelectedSlot("");
      setSelectedSlotLabel("");
      setIsSlotDisabled(true);
      setIsDateChanging(false);
    };
  }, []);

  useEffect(() => {
    // if(form?.values?.calendarId){
    // }
    setSlotOptions([]);
  }, [form?.values?.calendarId]);

  useEffect(() => {
    if (isCalendarDisabled) return;

    if (form.values?.endDate && form.values?.startDate) {
      const formatMinute = (minute) => minute.replace(/^0/, "");
      const slotTime = [
        moment.utc(form.values?.startDate).format("H"),
        formatMinute(moment.utc(form.values?.startDate).format("mm")),
        moment.utc(form.values?.endDate).format("H"),
        formatMinute(moment.utc(form.values?.endDate).format("mm")),
      ];

      setSelectedSlot(slotTime?.join(""));

      setIsSlotDisabled(false);
      if (form?.values?.endDate && !form?.values?.date) {
        form.setFieldValue(
          "date",
          moment
            .utc(form?.values?.startDate)
            .startOf("day")
            .format(time.format.isoStringFormat),
        );
        // Force slot fetch when date is auto-selected
        setShouldFetchSlots(true);
      }
      const start = moment.utc(form.values?.startDate).format("hh:mm A");
      const end = moment.utc(form.values?.endDate).format("hh:mm A");
      setSelectedSlotLabel(`${start} - ${end}`);
    } else {
      setSelectedSlotLabel("");
    }

    // Cleanup function to avoid setting state on unmounted component
    return () => {
      // setSelectedSlot("");
      setSelectedSlotLabel("");
    };
  }, [form.values?.endDate, isCalendarDisabled]);

  return (
    <>
      {/* {!isCalendarDisabled ? ( */}
      <Box
        className={`${styles.dateFieldWrapper} d-grid align-items-center`}
        sx={{ gap: 1 }}
      >
        <UIDatePicker
          name={"date"}
          value={calendarValue}
          title={"Date"}
          placeholder={"Select Date"}
          onChange={(date) => {
            setSelectedSlotLabel("");
            setSelectedSlot("");
            onChange(
              "date",
              moment(date).set("hours", 1).format(time.format.isoStringFormat),
            );
          }}
          onBlur={form.handleBlur}
          size="small"
          minDate={moment()}
          maxDate={moment().add(1, "years")}
          // format={"DD-MM-YYYY"}
          isLocal={true}
          error={form.touched["date"] && form.errors["date"] ? true : false}
          helperText={(form.touched["date"] && form.errors["date"]) || ""}
          wrapperClasss={styles.dateField}
          shouldDisableDate={shouldDisableDate}
          disabled={isCalendarDisabled || isReadOnly}
          sx={{
            width: "100%",
            "& .MuiFormHelperText-root": {
              position: "absolute",
              bottom: "-20px",
            },
          }}
          loadingDays={loadingDays}
          onMonthChange={(month) => {
            const monthString = month.format("YYYY-MM");
            setCurrentMonthView(month);
            if (!availableMonths.includes(monthString)) {
              setAvailableMonths([...availableMonths, monthString]);
              fetchAvailabilityForMonth(month.startOf("month"));
            }
          }}
          loadingText="Fetching available slots"
        />
        {!loadingDays?.length && isDateChanging && form?.values?.date ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1,
              mb: 1,
            }}
          >
            <CircularLoader sx={{ height: "auto" }} iconSize={"20px"} />
          </Box>
        ) : null}
        {form?.values?.date &&
        !isDateChanging &&
        slotOptions.length > 0 &&
        isDateAvailable !== false ? (
          <UISelect
            name="slot"
            value={selectedSlot || ""}
            title="Slot"
            onChange={(e) => {
              handleSlotChange(e);
            }}
            options={slotOptions}
            disabled={
              isSlotDisabled ||
              isCalendarDisabled ||
              isReadOnly ||
              !form?.values?.date ||
              isDateChanging
            }
            clearable={false}
            selectedSlotLabel={selectedSlotLabel}
            showNoData={true}
            noDataText="No slot available"
            variant="outlined"
            error={
              (form.touched["participantId"] && form.errors["participantId"]) ||
              (form.touched["startDate"] && form.errors["startDate"])
            }
          />
        ) : null}
      </Box>
    </>
  );
};

export default DateFields;
